import React from 'react';
import PropTypes from 'prop-types';
import { withStyles } from 'material-ui/styles';
import KeywordSearchControl from './KeywordSearchControl';
import ExcludeKeywordSearchControl from './ExcludeKeywordSearchControl';
import AndOrSearchControl from './AndOrSearchControl';
import { KEYWORD_SEARCH_IS_AND_CONFIG, KEYWORD_SEARCH_CONFIG } from '../../../constants/ExecutiveListSearchKeyConstants';

const styles = () => ({
  root: {
    width: '100%',
    marginBottom: '20px',
  },
});

/**
 * KEYWORD_SEARCH_CONFIGを活用した汎用キーワード検索コンポーネント
 * 設定オブジェクトに基づいて動的にキーワード検索UIを生成する
 * excludeのconfigが引数に来ることはありません。通常のkeywordからexcludeのconfigがあれば、excludeのconfigから出力します。
 */
class GenericKeywordSearchControl extends React.Component {
  render() {
    const { classes, config, state, handlers, disabled, user } = this.props;

    // 無効化ロジックの決定（優先度: getDisabled -> 特別ケース -> デフォルト）
    let computedDisabled = disabled;
    if (this.props.getDisabled) {
      computedDisabled = this.props.getDisabled(config, user, disabled);
    } else if (config.key === 'corporate_number_keyword') {
      computedDisabled = !(this.props.isEnableSearchExeByCorpNumbers && this.props.isEnableSearchExeByCorpNumbers(user)) || disabled;
    }

    return (
      <div className={classes.root}>
        {/* メインのキーワード検索 */}
        <div style={{ width: '100%' }}>
          <KeywordSearchControl
            typeName={config.ui.typeName}
            typeKey={this.getTypeKey(config.key)}
            placeholder={config.ui.placeholder}
            disabled={computedDisabled}
            keyword={state[config.key]}
            handleChange={handlers.handleChange}
            tooltipText={config.ui.tooltipText}
            toolTipPlacement={config.ui.toolTipPlacement}
            isNew={config.isNew || false}
          >
            {/* AND/OR検索コントロール（設定されている場合のみ） */}
            {this.getIsAndStateKey(config.key) && (
              <AndOrSearchControl
                typeKey={this.getTypeKey(config.key)}
                disabled={computedDisabled}
                isAndSearch={state[this.getIsAndStateKey(config.key)]}
                handleRadioChange={handlers.handleRadioChange}
              />
            )}
          </KeywordSearchControl>
        </div>

        {/* 除外キーワード検索（設定されている場合のみ） */}
        {this.hasExcludeKeyword(config.key) && (() => {
          const excludeConfig = this.getExcludeConfig(config.key);
          const tooltipText = (excludeConfig && excludeConfig.ui && excludeConfig.ui.tooltipText) || "入力した文字は空白を区切りとしてOR検索で除外されます.";
          return (
            <div style={{ width: '100%' }}>
              <ExcludeKeywordSearchControl
                typeKey={this.getTypeKey(config.key)}
                disabled={computedDisabled}
                keyword={state[this.getExcludeKey(config.key)]}
                handleChange={handlers.handleChange}
                showField={state[this.getShowExcludeFieldKey(config.key)] || false}
                toggleField={handlers.toggleExcludeTextField(this.getShowExcludeFieldKey(config.key))}
                tooltipText={tooltipText}
              >
              {/* 除外キーワード用のAND/OR検索コントロール（設定されている場合のみ） */}
              {this.getIsAndStateKey(this.getExcludeKey(config.key)) && this.hasExcludeAndOrSearch(config.key) && (
                <AndOrSearchControl
                  isExclude={true}
                  disabled={computedDisabled}
                  typeKey={this.getTypeKey(config.key)}
                  isAndSearch={state[this.getExcludeAndOrKey(config.key)]}
                  handleRadioChange={handlers.handleRadioChange}
                />
              )}
              </ExcludeKeywordSearchControl>
            </div>
          );
        })()}
      </div>
    );
  }

  /**
   * キーからタイプキーを取得
   * 例: "company_keyword" -> "company"
   */
  getTypeKey(key) {
    return key.replace('_keyword', '').replace('_exclude_keyword', '');
  }

  /**
   * 除外キーワードのキーを取得
   * 例: "company_keyword" -> "company_exclude_keyword"
   */
  getExcludeKey(key) {
    return key.replace('_keyword', '_exclude_keyword');
  }

  /**
   * 除外キーワードフィールド表示状態のキーを取得
   * 例: "company_keyword" -> "showCompanyExcludeKeywordTextfield"
   */
  getShowExcludeFieldKey(key) {
    const typeKey = this.getTypeKey(key);
    return `show${this.capitalizeFirst(typeKey)}ExcludeKeywordTextfield`;
  }

  /**
   * 除外キーワード用のAND/OR検索状態のキーを取得
   * 例: "exe_feature_keyword" -> "is_and_search_exe_feature_exclude_keyword"
   */
  getExcludeAndOrKey(key) {
    const typeKey = this.getTypeKey(key);
    return `is_and_search_${typeKey}_exclude_keyword`;
  }

  /**
   * AND/OR検索のstateKeyを設定から取得
   */
  getIsAndStateKey(key) {
    const typeKey = this.getTypeKey(key);
    const includeKey = `is_and_search_${typeKey}_keyword`;
    const excludeKey = `is_and_search_${typeKey}_exclude_keyword`;
    const existsInclude = !!KEYWORD_SEARCH_IS_AND_CONFIG[includeKey];
    const existsExclude = !!KEYWORD_SEARCH_IS_AND_CONFIG[excludeKey];
    if (existsInclude)  {
      return includeKey
    }
    if (existsExclude) {
      return excludeKey
    }
    return null;
  }

  /**
   * 除外キーワードが存在するかチェック
   */
  hasExcludeKeyword(key) {
    const excludeKey = this.getExcludeKey(key);
    return this.props.state[excludeKey] !== undefined;
  }

  /**
   * 除外キーワードの設定を取得
   */
  getExcludeConfig(key) {
    const excludeKey = this.getExcludeKey(key);
    return KEYWORD_SEARCH_CONFIG[excludeKey];
  }

  /**
   * 除外キーワード用のAND/OR検索が存在するかチェック
   */
  hasExcludeAndOrSearch(key) {
    const excludeAndOrKey = this.getExcludeAndOrKey(key);
    return this.props.state[excludeAndOrKey] !== undefined;
  }

  /**
   * 文字列の最初の文字を大文字にし、アンダースコアの後の文字も大文字にする
   * 例: "exe_feature" -> "ExeFeature"
   */
  capitalizeFirst(str) {
    return str
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join('');
  }
}

GenericKeywordSearchControl.propTypes = {
  classes: PropTypes.object.isRequired,
  config: PropTypes.object.isRequired,
  state: PropTypes.object.isRequired,
  handlers: PropTypes.shape({
    handleChange: PropTypes.func.isRequired,
    handleRadioChange: PropTypes.func.isRequired,
    toggleExcludeTextField: PropTypes.func.isRequired,
  }).isRequired,
  disabled: PropTypes.bool,
  user: PropTypes.object,
  isEnableSearchExeByCorpNumbers: PropTypes.func,
  getDisabled: PropTypes.func,
};

GenericKeywordSearchControl.defaultProps = {
  disabled: false,
  user: null,
  isEnableSearchExeByCorpNumbers: null,
  getDisabled: null,
};

export default withStyles(styles)(GenericKeywordSearchControl);



// WEBPACK FOOTER //
// ./src/components/ExecutiveList/controls/GenericKeywordSearchControl.jsx